import {cloneDeep, isFunction} from 'lodash';
import {ArrayUtil, StringUtils} from 'ac-infra';

export type TreeEntityTypeStandard = 'tenant' | 'region' | 'device' | 'link' | 'site' | 'ad';
export type TreeEntityTypeLiveCloud = 'tenant' | 'channel' | 'lcCustomer' | 'service' | 'siteLocation' | 'link';

export type TreeEntityType = TreeEntityTypeStandard | TreeEntityTypeLiveCloud;

export interface FlatEntities {
    [id: string | number]: any;
}

export type FlatEntitiesByType<T extends string = TreeEntityType> = {
    [entity in T]?: FlatEntities;
};

export interface TreeEntityInstruction<T = TreeEntityType> {
    parents?: T[];
    getParentId?: (entity: any) => string | number;
    artificialParentType?: T;
    artificialName?: string | (() => string);
    getEntities?: (tenantId: number, entityFilter?: (entity: any) => boolean) => FlatEntities;
    enrichEntities?: (entities: FlatEntities) => void;
    extend?: (entities) => any;
    iconNameField?: string;
}

export type TreeEntitiesInstructions<T extends string = TreeEntityType> = {
    [entity in T]?: TreeEntityInstruction<T>;
};


export type FiltersByEntity<T extends string = TreeEntityType> = {
    [entity in T]?: (entity: any, ...args: any[]) => boolean;
};

export interface BuildTreeOptions<T extends string = TreeEntityType> {
    filtersByEntity?: FiltersByEntity<T>;
    excludeArtificial?: boolean;
}

export class NetworkBaseTreeBuilderService<T extends string = TreeEntityType> {

    constructor(protected defaultEntityTypesList: T[], protected treeEntitiesInstructions: TreeEntitiesInstructions<T>) {
    }

    getTopologyTreeData = (entitiesType?: T[], tenantId?: string | number, options: BuildTreeOptions<T> = {}) => {
        if (!entitiesType) {
            entitiesType = this.defaultEntityTypesList;
        }

        const {filtersByEntity, excludeArtificial} = options;

        const entitiesCache = ArrayUtil.arrayToObject(entitiesType, (cache, entityType) => {
            const {getEntities} = this.treeEntitiesInstructions[entityType];
            const entityFilter = filtersByEntity?.[entityType];
            cache[entityType] = cloneDeep(getEntities(tenantId, entityFilter));
        });

        entitiesType.forEach((entityType) => {
            const {enrichEntities} = this.treeEntitiesInstructions[entityType];
            if (isFunction(enrichEntities)) {
                enrichEntities(entitiesCache);
            }
        });

        entitiesType.forEach((entityType) => {
            const {parents} = this.treeEntitiesInstructions[entityType];
            if (!parents) {
                return;
            }
            const entities = entitiesCache[entityType];
            const parentEntities = ArrayUtil.arrayToObject(parents, (cache, parentType) => {
                Object.assign(cache, entitiesCache[parentType]);
            });
            this.fillParents(parentEntities, entities, entityType, excludeArtificial);
        });

        return Object.values(entitiesCache.tenant);
    };

    fillParents = (parents: FlatEntities, entities: FlatEntities, entityType: T, excludeArtificial: boolean) => {
        if (!entities || !parents) {
            return;
        }

        const flatArtificialParents = {};
        const artificialParentType = this.treeEntitiesInstructions[entityType].artificialParentType;
        const getParentId = this.treeEntitiesInstructions[entityType].getParentId;

        Object.values(entities).forEach((entity) => {
            const parentId = getParentId(entity);
            const parent = parents[parentId];

            if (!parent) {
                return;
            }

            if (!parent.children) {
                parent.children = [];
            }

            if (excludeArtificial || artificialParentType !== parent.entityType) {
                parent.children.push(entity);
                return;
            }

            let artificialParent = flatArtificialParents[parent.id];

            if (!artificialParent) {
                artificialParent = this.getBaseArtificialParent(parent, entity);
                flatArtificialParents[parent.id] = artificialParent;
                parent.children.push(artificialParent);
            }

            artificialParent.children.push(entity);
        });
    };

    getBaseArtificialParent = (parent: any, child: any) => {
        const entityType = child.entityType + 's';
        const id = `${parent.entityType}-${parent.id}-${entityType}`;

        let artificialName = this.treeEntitiesInstructions[child.entityType].artificialName || entityType;
        if (isFunction(artificialName)) {
            artificialName = artificialName();
        }

        return {
            artificial: true,
            id,
            entityType,
            parentId: parent.id,
            parentEntityType: parent.entityType,
            name: StringUtils.toTitleCase(artificialName, {onlyLeadLetter: true}),
            children: [],
        };
    };

    needArtificialParent = (entityType: TreeEntityTypeLiveCloud, parentEntityType: TreeEntityTypeLiveCloud = null) => {
        return this.treeEntitiesInstructions[entityType].artificialParentType === parentEntityType;
    };


    getNativeParentId = (entity: any): number | string => {
        const getParentId = this.treeEntitiesInstructions[entity.entityType]?.getParentId;
        return getParentId ? getParentId(entity) : null;
    };
}
